import {
  UserPreferencesState,
  RootState,
  WorkflowJobPreference,
  WorkflowTreeView,
  TableStateModel,
  Dictionary,
  DocumentsToStorePreference,
  StoreConstants,
  TableSortModel,
  TableFilterModel,
  ColumnConfiguration,
  PagesStoreMode
} from '@/types';
import { GetterTree } from 'vuex';
import { defaultUserPreferencesState, userPreferences } from './index';
import { defaultMtableState } from '../mtable';
import { TableSortPropertiesPreference } from '@/types/store/userPreferences/TableSortPropertiesPreference';

export const getters: GetterTree<UserPreferencesState[], RootState> = {
  getWorkflowJobPreferences: (state, getters, rootState: any) => (queueId: number): WorkflowJobPreference => {
    const userPreference = getters.getUserPreferenceState();

    if (!userPreference) {
      return userPreferences.defaultUserPreferencesState()[0].workflowJob.workflowJobPreferences[0];
    } else {
      const workflowJobPreference = userPreference.workflowJob.workflowJobPreferences.find((x: any) => x.queueId === queueId);
      return workflowJobPreference ? workflowJobPreference : userPreferences.defaultUserPreferencesState()[0].workflowJob.workflowJobPreferences[0];
    }
  },

  getWorkflowTreeViewPreferences: (state, getters, rootState: any) => (): WorkflowTreeView => {
    const userPreference = getters.getUserPreferenceState();

    return userPreference && userPreference.workflowTreeView != null ? userPreference.workflowTreeView : userPreferences.defaultUserPreferencesState()[0].workflowTreeView;
  },

  getSelectedWorkflowQueue: (state, getters, rootState: any) => (): number => {
    const userPreference = getters.getUserPreferenceState();
    return userPreference && userPreference.workflowJob != null ? userPreference.workflowJob.SelectedWorkflowQueueID : 0;
  },

  getUserPreferedTableStateField: (state, getters) => (queueId: number, field: keyof TableStateModel): any => {
    const workflowQueuePreference = getters.getWorkflowJobPreferences(queueId);

    return workflowQueuePreference.tableOptions ? workflowQueuePreference.tableOptions[field] : defaultMtableState()[field];
  },

  getUserPreferenceState: (state, getters, rootState: any) => (): UserPreferencesState | undefined => {
    return state.find((x: any) => x.userId === Number(rootState.oidcStore.user.id));
  },

  getDocumentToStoreCanGetNextDocument: (state, getters): boolean | null => {
    const userPreference = getters.getUserPreferenceState();
    return userPreference?.documentToStore?.canGetNextDocument;
  },

  getDocumentToStoreTargetDocumentType: (state, getters, rootState: any) => (documentToStoreSourceDocumentType: string): number | null => {
    const userPreference = getters.getUserPreferenceState();
    if (!userPreference?.documentToStore?.targetDocumentTypes) {
      return null;
    }
    return userPreference.documentToStore.targetDocumentTypes[documentToStoreSourceDocumentType] || null;
  },
  getDocumentToStoreTargetDocumentTypes: (state, getters, rootState: any): Dictionary<number> | null => {
    const userPreference = getters.getUserPreferenceState();

    return userPreference?.documentToStore?.targetDocumentTypes;
  },
  getDocumentToStoreDocumentTypeSelection: (state, getters, rootState: any) => (documentToStoreSourceDocumentType: string, cabinetId: string): number | null => {
    const userPreference = getters.getUserPreferenceState();

    if (!userPreference?.documentToStore?.documentTypeSelections || !userPreference.documentToStore.documentTypeSelections[documentToStoreSourceDocumentType]) {
      return null;
    }
    return userPreference.documentToStore.documentTypeSelections[documentToStoreSourceDocumentType][cabinetId] || null;
  },
  getPinFilter: (state, getters) => (queueId: number): boolean => {
    return getters?.getWorkflowJobPreferences(queueId)?.pinFilter || false;
  },
  getIsHorizontalTabUp: (state, getters) => (queueId: number): boolean => {
    return getters?.getWorkflowJobPreferences(queueId)?.isHorizontalTabUp || false;
  },
  suppressedConfirmations: (state, getters) => (): string[] => {
    const userPreference = getters.getUserPreferenceState();
    return userPreference?.suppressedConfirmations || [];
  },
  isConfirmationSupressed: (state, getters) => (key: string): boolean => {
    const suppressedConfirmations = getters.suppressedConfirmations();
    return suppressedConfirmations.includes(key);
  },
  getDocumentsToStoreTableSortProperties: (state, getters) => (): TableSortPropertiesPreference => {
    const userPreference = getters.getUserPreferenceState();

    if (userPreference?.documentsToStore?.tableSortPropertiesPreference) {
      return userPreference.documentsToStore.tableSortPropertiesPreference;
    } else {
      return userPreferences.defaultUserPreferencesState()[0].documentsToStore.tableSortPropertiesPreference;
    }
  },
  getDocumentsToStorePreferences: (state, getters) => (documentType: any): DocumentsToStorePreference | undefined => {
    const userPreference = getters.getUserPreferenceState();

    if (userPreference?.documentsToStore?.documentsToStorePreference && documentType) {
      return userPreference.documentsToStore.documentsToStorePreference.find((x: any) => x.documentType === documentType);
    }
  },
  getDocumentToStoreTableFilters: (state, getters) => (): any => {
    const userPreference = getters.getUserPreferenceState();

    if (userPreference?.documentToStore?.tableFilters) {
      return userPreference.documentToStore.tableFilters;
    } else {
      return userPreferences.defaultUserPreferencesState()[0].documentToStore.tableFilters;
    }
  },
  [StoreConstants.UserPreferences.Getters.SortOptions]: (state, getters) => (queueId: string): TableSortModel[] => {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.workflowQueue?.sortOptions?.[queueId] || [];
  },
  [StoreConstants.UserPreferences.Getters.FilterOptions]: (state, getters) => (queueId: string): TableFilterModel[] => {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.workflowQueue?.filterOptions?.[queueId] || [];
  },
  [StoreConstants.UserPreferences.Getters.PinSort]: (state, getters) => (queueId: string): boolean => {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.workflowQueue?.pinSort?.[queueId] ?? false;
  },
  [StoreConstants.UserPreferences.Getters.PagesStoreMode](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.documentToStore?.pagesStoreMode ?? PagesStoreMode.ManualPageSelection;
  },
  [StoreConstants.UserPreferences.Getters.ColumnConfigurations]: (state, getters) => (queueId: string): ColumnConfiguration[] => {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.workflowQueue?.columnConfigurations?.[queueId] ?? [];
  },
  [StoreConstants.UserPreferences.Getters.EmailToMe](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.emailToMe ?? defaultUserPreferencesState()[0].emailToMe;
  },
  [StoreConstants.UserPreferences.Getters.SearchOptions](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.search ?? defaultUserPreferencesState()[0].search;
  },
  [StoreConstants.UserPreferences.Getters.SearchPinSort](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.search?.pinSort ?? defaultUserPreferencesState()[0].search.pinSort;
  },
  [StoreConstants.UserPreferences.Getters.SearchPinFilter](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.search?.pinFilter ?? defaultUserPreferencesState()[0].search.pinFilter;
  },
  [StoreConstants.UserPreferences.Getters.SearchSplitConfiguration](state, getters) {
    const userPreference = getters.getUserPreferenceState() as UserPreferencesState | undefined;

    return userPreference?.search?.splitConfiguration ?? defaultUserPreferencesState()[0].search.splitConfiguration;
  }
};
